import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getCollections, prefetchCollectionItems } from "../utils/collectionsData";
import { seededShuffleForDay } from "../utils/dailyRandom";
import { useInViewOnce } from "../hooks/useInViewOnce";
import "./Collections.css";

/** Title derivation for when the API response is missing `name` (matches backend's fallback). */
const titleFromKey = (key) => key.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Load preview thumbnails for the first rows immediately; defer decoding for the rest. */
const COLLECTION_CARD_PRIORITY = 12;

function CollectionCard({
  collection,
  loading,
  idx,
  getGridClass,
  getImageUrl,
  handleImageLoad,
  imgRefs,
  navigate,
}) {
  const [wrapRef, showThumb] = useInViewOnce({
    skip: idx < COLLECTION_CARD_PRIORITY,
    rootMargin: "320px 0px",
  });
  const url = getImageUrl(collection);

  return (
    <div
      ref={wrapRef}
      className={`collection-card ${getGridClass(collection)} ${loading ? "loading" : ""}`}
      onClick={() => navigate(`/collections/${collection.key}`)}
      onMouseEnter={() => prefetchCollectionItems(collection.key)}
      onFocus={() => prefetchCollectionItems(collection.key)}
      onTouchStart={() => prefetchCollectionItems(collection.key)}
    >
      <div className="card-content">
        {showThumb ? (
          <img
            ref={(el) => {
              imgRefs.current[collection.key] = el;
            }}
            src={url}
            alt={collection.title}
            loading={idx < COLLECTION_CARD_PRIORITY ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={idx < COLLECTION_CARD_PRIORITY ? "high" : "low"}
            onLoad={() => handleImageLoad(collection)}
            onError={(e) => {
              console.error(`Failed to load thumbnail for ${collection.key}`);
              e.target.src = collection.fallbackImg;
            }}
          />
        ) : (
          <div
            className="collection-card-placeholder"
            aria-hidden
          />
        )}
        <div className="card-overlay">
          <span className="card-title">{collection.title}</span>
        </div>
      </div>
    </div>
  );
}

const Collections = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageAspects, setImageAspects] = useState({});
  const imgRefs = useRef({});

  useEffect(() => {
    const fetchCollections = async () => {
      setLoading(true);
      try {
        const rows = await getCollections();
        const withMedia = rows.filter((c) => Number(c?.count) > 0);

        const mapped = withMedia.map((collection) => {
          const title = collection.name || collection.title || titleFromKey(collection.key);
          return {
            key: collection.key,
            title,
            thumbnailUrl: collection.thumbnailUrl,
            width: collection.width,
            height: collection.height,
            fallbackImg: `https://placehold.co/800x600?text=${encodeURIComponent(title)}`,
          };
        });

        setCollections(seededShuffleForDay(mapped, "collections-tab"));
      } catch (err) {
        console.error("Error fetching collections:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, []);

  // Get grid class based on image aspect ratio (similar to Portfolio)
  const getGridClass = (collection) => {
    // If we have width/height from API
    if (collection.width && collection.height) {
      const ratio = collection.width / collection.height;
      if (ratio > 2) return "grid-panorama";
      if (ratio < 1) return "grid-vertical";
      return "grid-1x1";
    }
    
    // If we have measured the image
    if (imageAspects[collection.key]) {
      const ratio = imageAspects[collection.key];
      if (ratio > 2) return "grid-panorama";
      if (ratio < 1) return "grid-vertical";
      return "grid-1x1";
    }
    
    // Default
    return "grid-1x1";
  };

  // Handle image load to measure aspect ratio
  const handleImageLoad = (collection) => {
    const img = imgRefs.current[collection.key];
    if (img && img.naturalWidth && img.naturalHeight) {
      const ratio = img.naturalWidth / img.naturalHeight;
      setImageAspects(prev => ({...prev, [collection.key]: ratio}));
    }
  };

  // Get thumbnail URL (real image or fallback)
  const getImageUrl = (collection) => {
    return collection.thumbnailUrl || collection.fallbackImg;
  };

  if (error && !collections.length) {
    return (
      <section className="collections">
        <h1 className="collections-title">Collections</h1>
        <div className="collections-error">Failed to load collections. Please try again later.</div>
      </section>
    );
  }

  return (
    <section className="collections">
      <h1 className="collections-title">Collections</h1>
      {loading && collections.length === 0 && <div className="collections-loading">Loading collections...</div>}
      {!loading && collections.length === 0 && !error && (
        <p className="collections-empty">No collections with images yet.</p>
      )}
      <div className="collections-grid">
        {collections.map((collection, idx) => (
          <CollectionCard
            key={collection.key}
            collection={collection}
            idx={idx}
            loading={loading}
            getGridClass={getGridClass}
            getImageUrl={getImageUrl}
            handleImageLoad={handleImageLoad}
            imgRefs={imgRefs}
            navigate={navigate}
          />
        ))}
      </div>
    </section>
  );
};

export default Collections;